import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import MainLoader from "../../../components/loading/MainLoader";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import Title from "../../../components/title/Title";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../../const/URL";
import { FiEye } from "react-icons/fi";
import Button from "@mui/material/Button";

const statusColors: { [key: string]: string } = {
  REQUESTED: "#4caf50",
  ACKNOWLEDGED: "#ff9800",
  COMPLETED: "#2196f3",
};

const useDynamicColumnWidth = (columnCount: number) => {
  const [columnWidth, setColumnWidth] = useState(0);

  useEffect(() => {
    const calculateWidth = () => {
      const availableWidth = window.innerWidth - 410;
      return Math.max(100, availableWidth / columnCount);
    };

    setColumnWidth(calculateWidth());

    const handleResize = () => {
      setColumnWidth(calculateWidth());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [columnCount]);

  return columnWidth;
};

const ServiceRequestHistory = () => {
  const columnCount = 6;
  const columnWidth = useDynamicColumnWidth(columnCount);

  const columns: GridColDef[] = [
    //   { field: "id", headerName: "ID", width: 100 },
    {
      field: "crmReference",
      headerName: "Reference",
      width: Number(columnWidth),
    },
    // { field: "entReference", headerName: "ENT Reference", width: 150 },
    { field: "subject", headerName: "Subject", width: Number(columnWidth) },
    {
      field: "submittedBy",
      headerName: "Submitted By",
      width: Number(columnWidth),
    },
    //   { field: "contactPersonFirstName", headerName: "First Name", width: 150 },
    //   { field: "contactPersonLastName", headerName: "Last Name", width: 150 },
    // { field: "contactPersonMobilePhone", headerName: "Mobile Phone", width: 150 },
    //   { field: "serviceAddress", headerName: "Service Address", width: 200 },
    //   {
    //     field: "serviceDescription",
    //     headerName: "Service Description",
    //     width: 300,
    //   },
    {
      field: "submittedDate",
      headerName: "Submitted Date",
      width: Number(columnWidth),
    },
    // { field: "status", headerName: "Status", width: 130 },
    {
      field: "status",
      headerName: "Status",
      width: Number(columnWidth),
      renderCell: (params) => (
        <span
          style={{
            padding: "5px 10px",
            borderRadius: "15px",
            color: "#fff",
            backgroundColor: statusColors[params.value] || "#607d8b",
            fontWeight: "bold",
            textTransform: "capitalize",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: Number(columnWidth),
      renderCell: (params) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const navigate = useNavigate();
        return (
          <Button
            onClick={() =>
              navigate(`/tracker/status/serviceRequestProgress`, {
                state: { srNumber: params.row.crmReference },
              })
            }
            startIcon={
              <FiEye
                style={{
                  color: "#143D60",
                  alignContent: "center",
                  alignItems: "center",
                }}
              />
            }
            style={{
              padding: "10px",
              color: "white",
              alignContent: "center",
              alignItems: "center",
              border: "none",
              borderRadius: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          ></Button>
        );
      },
    },
  ];

  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<any[]>([]);

  const paginationModel = { page: 0, pageSize: 10 };

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const response = await axios.post(
        `${BASE_URL}/api/service-requests`,
        {
          statuses: ["IN_PROGRESS", "REQUESTED", "ACKNOWLEDGED", "COMPLETED"],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.isSuccess) {
        const formattedRows = response.data.dataBundle.map((item: any) => ({
          id: item.id,
          crmReference: item.crmReference,
          entReference: item.entReference,
          subject: item.subject,
          submittedBy: item.submittedBy,
          contactPersonFirstName: item.contactPersonFirstName,
          contactPersonLastName: item.contactPersonLastName,
          contactPersonMobilePhone: item.contactPersonMobilePhone,
          serviceAddress: item.serviceAddress,
          serviceDescription: item.serviceDescription,
          submittedDate: new Date(item.submittedDate).toLocaleString(),
          status: item.status,
        }));
        setRows(formattedRows);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return (
    <div className="page-container">
      <MainLoader show={loading} />

      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          {
            path: "/serviceRequest/newServiceHistory",
            title: "Service Request History",
          },
        ]}
      />
      <Title>Service Request History</Title>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          height: "100%",
          minHeight: 300,
          marginTop: "10px",
          overflow: "auto",
          p: 1,
          mx: "auto",
          width: "100%",
          "& .MuiTablePagination-root": {
            display: "flex",
            alignItems: "baseline",
            justifyContent: "flex-end",
            minHeight: "56px",
            gap: "8px",
          },
          "& .MuiTablePagination-spacer": {
            order: 2,
          },
          "& .MuiTablePagination-selectLabel": {
            order: 0,
            display: "flex",
            alignItems: "baseline",
            padding: "15px",
            height: "36px",
          },
          "& .MuiTablePagination-select": {
            order: 1,
            display: "flex",
            alignItems: "center",
            height: "36px",
          },
          "& .MuiTablePagination-displayedRows": {
            order: 3,
            display: "flex",
            padding: "15px",
            alignItems: "baseline",
            height: "36px",
          },
          "& .MuiTablePagination-actions": {
            order: 4,
            display: "flex",
            alignItems: "center",
            marginLeft: "auto",
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{ pagination: { paginationModel } }}
          pageSizeOptions={[5, 10, 15]}
          sx={{ border: 0 }}
        />
      </Paper>
    </div>
  );
};

export default ServiceRequestHistory;
